/**
 * Session Engine v3 — canonical engine logic.
 *
 * Pure logic helpers: per-step tool recommendation, item-input capability
 * resolution (conditions-met gating), S1–S5 dependency matrix, step status
 * and "next right thing" recommendation, exclusivity evaluation, ranking
 * computation, and step-satisfaction / required-information checks.
 *
 * Production wiring pointers:
 *   - tool specs:      src/generated/registryToolSpecs.ts
 *   - relationships:   src/stores/prioritization/itemRelationshipsStore.ts
 *   - anti-patterns:   src/lib/poolViolations.ts
 */

import {
  REGISTRY_TOOL_SPECS_BY_ID,
  REGISTRY_SESSION_TOOL_IDS,
} from '../../generated/registryToolSpecs';
import { toolAsksForCriteria } from '../../lib/toolCriteria';
import type {
  ItemRelationshipGroup,
  ItemRelationshipGroupMembership,
} from '../../types/prioritization/itemRelationships';
import type {
  ContextMode,
  EngineItem,
  SessionEvent,
  ExclusivityGroup,
  ItemInputSource,
  PoolState,
  StepId,
  StepMode,
} from './types';
import { STEP_ORDER } from './types';
import { getJob } from './sessionJobs';
import { isToolSwitchedOff } from '../../lib/switchedOffTools';

// ───────────────────────────────────────────────────────────────────────────
// Tool recommendation (question 2 of the 3-question framing)
// ───────────────────────────────────────────────────────────────────────────

/** Which registry tools naturally fit each step's intent. */
const STEP_TOOL_HINTS: Record<StepId, string[]> = {
  S1: ['dot_voting'],
  S2: ['dot_voting', 'moscow', 'impact_effort_matrix', 'pareto_analysis', 'stack_ranking', 'magic_prioritization'],
  S3: ['dot_voting', 'pairwise_ranking'],
  S4: ['simple_weighted_scoring', 'rice', 'wsjf', 'cost_of_delay'],
  S5: ['stack_ranking', 'pairwise_ranking', 'simple_weighted_scoring', 'rice', 'wsjf'],
};

export interface ToolOption {
  toolId: string;
  displayName: string;
  description: string;
  /** True when the current item count is within the tool's supported range. */
  fitsItemCount: boolean;
  fitNote?: string;
}

/**
 * Recommend tools for a step, ordered best-first, annotated with whether the
 * current item count fits the tool. Tools the registry does not expose for
 * sessions are filtered out.
 */
export function recommendToolsForStep(step: StepId, itemCount: number, jobId?: string): ToolOption[] {
  const sessionTools = new Set(REGISTRY_SESSION_TOOL_IDS);
  const job = getJob(jobId);
  const jobHints = job?.steps.find((spec) => spec.step === step)?.recommendedToolIds ?? [];
  const hints = [...new Set([...jobHints, ...(STEP_TOOL_HINTS[step] ?? [])])];
  return hints
    .filter((id) => sessionTools.has(id) && !isToolSwitchedOff(id))
    .map((toolId) => {
      const spec = REGISTRY_TOOL_SPECS_BY_ID[toolId];
      const min = spec?.compatibility.minItems ?? 0;
      const max = spec?.compatibility.maxItems;
      const fitsItemCount = itemCount >= min && (max == null || itemCount <= max);
      let fitNote: string | undefined;
      if (!fitsItemCount) {
        if (itemCount < min) fitNote = `needs at least ${min} items`;
        else if (max != null) fitNote = `best for ${max} or fewer items`;
      }
      return {
        toolId,
        displayName: spec?.displayName ?? toolId,
        description: spec?.description ?? '',
        fitsItemCount,
        fitNote,
      };
    });
}

// ───────────────────────────────────────────────────────────────────────────
// Item-input capabilities (conditions-met gating — see SPEC-01)
// ───────────────────────────────────────────────────────────────────────────

export interface ItemInputCapability {
  available: boolean;
  reason?: string;
}

export type ItemInputCapabilities = Record<ItemInputSource, ItemInputCapability>;

export function resolveItemInputCapabilities(input: {
  contextMode: ContextMode;
  poolState: PoolState;
  stepMode?: StepMode;
  isFacilitator?: boolean;
}): ItemInputCapabilities {
  const { contextMode, poolState, isFacilitator = true } = input;

  if (poolState === 'locked') {
    const locked: ItemInputCapability = { available: false, reason: 'Pool is locked — reopen the pool to add items' };
    return { import: locked, pull: locked, type: locked, platform: locked, collection: locked };
  }

  const platformAvailable = contextMode !== 'isolated';
  return {
    import: isFacilitator
      ? { available: true }
      : { available: false, reason: 'Only the facilitator can import items for now' },
    pull: { available: true },
    type: { available: true },
    platform: platformAvailable
      ? { available: true }
      : { available: false, reason: 'No workspace binding in an isolated session' },
    collection: platformAvailable
      ? { available: true }
      : { available: false, reason: 'No workspace binding in an isolated session' },
  };
}

export const ITEM_SOURCE_META: Record<ItemInputSource, { label: string; blurb: string }> = {
  import: { label: 'Import / export', blurb: 'Bring items in from a file (CSV / paste)' },
  pull: { label: 'Pull from connector', blurb: 'From Jira, Linear, GitHub Issues, or another connector' },
  type: { label: 'Type it in', blurb: 'Create items inline by typing' },
  platform: { label: 'Use items in the platform', blurb: 'Select items that already exist' },
  collection: { label: 'From a collection', blurb: 'Add all items from a named collection at once' },
};

// ───────────────────────────────────────────────────────────────────────────
// Exclusivity (see SPEC-02)
// ───────────────────────────────────────────────────────────────────────────

/**
 * Derive prototype exclusivity groups from the real item-relationship store.
 * Relationships are authored on the item detail page (not in the session) — we
 * only read the `exclusive` groups here and surface them, read-only, in
 * scenario planning. competing_alternative → competing (warn); incompatible →
 * cannot_coexist (block).
 */
export function deriveExclusivityGroups(
  groups: ItemRelationshipGroup[],
  memberships: ItemRelationshipGroupMembership[],
  poolItemIds: string[],
): ExclusivityGroup[] {
  const poolSet = new Set(poolItemIds);
  const membersByGroup = new Map<string, string[]>();
  for (const m of memberships) {
    if (!poolSet.has(m.itemId)) continue;
    const arr = membersByGroup.get(m.groupId) ?? [];
    arr.push(m.itemId);
    membersByGroup.set(m.groupId, arr);
  }
  const out: ExclusivityGroup[] = [];
  for (const g of groups) {
    if (g.type !== 'exclusive') continue;
    if (g.status === 'dismissed' || g.status === 'dissolved') continue;
    const members = membersByGroup.get(g.id) ?? [];
    if (members.length < 2) continue;
    out.push({
      id: g.id,
      kind: g.subtype === 'incompatible' ? 'cannot_coexist' : 'competing',
      itemIds: members,
      label: g.title,
    });
  }
  return out;
}

export interface ExclusivityWarning {
  groupId: string;
  label?: string;
  itemIds: string[];
  message: string;
}

export interface ExclusivityFindings {
  /** competing members co-selected above the cut line */
  warnings: ExclusivityWarning[];
  /** cannot-coexist members present in the same pool */
  blocks: ExclusivityWarning[];
}

/**
 * Evaluate exclusivity for a set of pool items and a selected subset (above the
 * cut line). competing → warn when 2+ selected together; cannot_coexist → block
 * when 2+ present in the pool at all.
 */
/**
 * Pure: accept cut line → keep only above-cut items as the forward pool.
 * Order follows `rankedItemIds` (above-cut slice). Values for dropped items
 * are pruned; ranking becomes the kept ids in cut order.
 */
export function applyCutLineToPoolItems(input: {
  items: EngineItem[];
  values: Record<string, Record<string, number>>;
  ranking: string[];
  rankedItemIds: string[];
  cutLine: number;
}): {
  items: EngineItem[];
  values: Record<string, Record<string, number>>;
  ranking: string[];
  keptIds: string[];
  droppedIds: string[];
} {
  const { items, values, rankedItemIds } = input;
  const cut = Math.max(0, Math.min(input.cutLine, rankedItemIds.length));
  const keptIds = rankedItemIds.slice(0, cut);
  const keptSet = new Set(keptIds);
  const byId = new Map(items.map((i) => [i.id, i]));
  const nextItems = keptIds.map((id) => byId.get(id)).filter((i): i is EngineItem => i != null);
  // Preserve any kept ids that weren't in the ranked list (shouldn't happen)
  for (const item of items) {
    if (keptSet.has(item.id) && !nextItems.some((i) => i.id === item.id)) {
      nextItems.push(item);
    }
  }
  const nextValues: Record<string, Record<string, number>> = {};
  for (const id of keptIds) {
    if (values[id]) nextValues[id] = values[id];
  }
  const droppedIds = items.map((i) => i.id).filter((id) => !keptSet.has(id));
  return {
    items: nextItems,
    values: nextValues,
    ranking: keptIds,
    keptIds,
    droppedIds,
  };
}

export function evaluateExclusivity(
  groups: ExclusivityGroup[],
  poolItemIds: string[],
  selectedItemIds: string[],
): ExclusivityFindings {
  const poolSet = new Set(poolItemIds);
  const selectedSet = new Set(selectedItemIds);
  const warnings: ExclusivityWarning[] = [];
  const blocks: ExclusivityWarning[] = [];

  for (const group of groups) {
    const inPool = group.itemIds.filter((id) => poolSet.has(id));
    if (group.kind === 'cannot_coexist') {
      if (inPool.length >= 2) {
        blocks.push({
          groupId: group.id,
          label: group.label,
          itemIds: inPool,
          message: 'These items cannot be in the same session/pool',
        });
      }
    } else {
      const selected = group.itemIds.filter((id) => selectedSet.has(id));
      if (selected.length >= 2) {
        warnings.push({
          groupId: group.id,
          label: group.label,
          itemIds: selected,
          message: 'Competing items are both above the cut line — at most one should win',
        });
      }
    }
  }
  return { warnings, blocks };
}

/**
 * Inclusion-time check: would adding `candidateId` violate a cannot-coexist rule
 * against items already in the pool? Returns the conflicting item ids (empty = ok).
 */
export function cannotCoexistConflicts(
  groups: ExclusivityGroup[],
  poolItemIds: string[],
  candidateId: string,
): string[] {
  const poolSet = new Set(poolItemIds);
  const conflicts = new Set<string>();
  for (const group of groups) {
    if (group.kind !== 'cannot_coexist') continue;
    if (!group.itemIds.includes(candidateId)) continue;
    for (const id of group.itemIds) {
      if (id !== candidateId && poolSet.has(id)) conflicts.add(id);
    }
  }
  return [...conflicts];
}

// ───────────────────────────────────────────────────────────────────────────
// Ranking computation (S5)
// ───────────────────────────────────────────────────────────────────────────

/** Weighted-sum score for an item across all criteria. */
export function scoreItem(event: SessionEvent, itemId: string): number {
  const values = event.values[itemId] ?? {};
  let total = 0;
  for (const c of event.criteria) {
    total += (values[c.id] ?? 0) * c.weight;
  }
  return total;
}

/** Ranked item ids by descending weighted score (deterministic; ties keep input order). */
export function computeRanking(event: SessionEvent): string[] {
  return [...event.items]
    .map((it, idx) => ({ id: it.id, idx, score: scoreItem(event, it.id) }))
    .sort((a, b) => (b.score - a.score) || (a.idx - b.idx))
    .map((x) => x.id);
}

// ───────────────────────────────────────────────────────────────────────────
// Step satisfaction & required information (conditions-met readiness)
// ───────────────────────────────────────────────────────────────────────────

/**
 * Content present for Frame / conditions-met (participants, items, …).
 * Does not require a collaborative tool or completed run — those belong to
 * the Main Event sitting.
 */
export function isStepContentSatisfied(event: SessionEvent, step: StepId): boolean {
  switch (step) {
    case 'S1':
      return event.participants.length > 0;
    case 'S2':
      return event.items.length > 0;
    case 'S3':
      return event.criteria.length > 0;
    case 'S4': {
      if (event.items.length === 0 || event.criteria.length === 0) return false;
      return event.items.every((it) =>
        event.criteria.every((c) => typeof event.values[it.id]?.[c.id] === 'number'),
      );
    }
    case 'S5':
      return event.ranking.length > 0;
    default:
      return false;
  }
}

/**
 * Whether the step's Frame conditions are met for dependency / navigation.
 * Collaborative tool selection and Go live are Main Event concerns — not
 * required here (so goal=S2 can declare an item pool before S3/S4).
 */
export function isStepSatisfied(event: SessionEvent, step: StepId): boolean {
  return isStepContentSatisfied(event, step);
}

/** Main Event collaborative sitting finished (or N/A for declared steps). */
export function isStepRunComplete(event: SessionEvent, step: StepId): boolean {
  const cfg = event.steps[step];
  if (cfg.mode !== 'collaborative') return isStepContentSatisfied(event, step);
  return cfg.runStatus === 'complete';
}

function isRequiredPrelimSatisfiedForRanking(event: SessionEvent, step: StepId): boolean {
  const cfg = event.steps[step];
  if (cfg.mode === 'collaborative') {
    // When this step is the primary goal, its collaborative run is the Main
    // Event — Frame only needs content so other prelims (e.g. S3/S4) can proceed.
    if (event.primaryGoalStep === step) {
      return isStepContentSatisfied(event, step);
    }
    return cfg.runStatus === 'complete';
  }
  return isStepContentSatisfied(event, step);
}

// ───────────────────────────────────────────────────────────────────────────
// Hard prerequisites — what a step genuinely cannot be answered without
// (owner ruling 2026-09-22)
//
// Canon §01 "S1–S5 Dependency Matrix" is a CONVERGENCE / quality model: it
// names which steps inform each other and closes with the explicit exception
// that "any step can be declared manually (bypassing the dependency gate) by
// the facilitator". STEP_DEPENDENCIES below still encodes that advisory model
// (it drives goodness, step status and reconfirm prompts). What follows is the
// separate, much smaller set of HARD prerequisites the setup and run gates use:
//
//   • Collaborative anything needs participants (S1). Without a roster there is
//     nobody to collaborate with, so the step is declared-only — offered with
//     the reason said out loud, never silently hidden.
//   • S1, S2 and S3 declared need nothing. Criteria and values are offers.
//   • S4 (values) needs items (S2) and criteria (S3) — there is nothing to
//     score otherwise.
//   • S5 (ranking) needs items (S2), plus criteria (S3) and values (S4) ONLY
//     when the chosen tool scores against criteria the facilitator picks
//     (simple weighted scoring). Stack ranking, pairwise and dot voting use no
//     criteria; RICE, WSJF, cost of delay and impact/effort carry their own
//     and collect them inside the tool. Read off the one shared predicate,
//     `toolAsksForCriteria` (src/lib/toolCriteria.ts) — the session wizard asks the
//     same question, so the two setups can never disagree.
// ───────────────────────────────────────────────────────────────────────────

/** True when the tool scores items against criteria the facilitator picks, so S3/S4 are real inputs. */
export function toolUsesCriteria(toolId: string | null | undefined): boolean {
  return toolAsksForCriteria(toolId);
}

/**
 * Is `step` satisfied well enough to serve as another step's prerequisite?
 *
 * Participants count by presence alone: a declared roster is somebody to
 * collaborate with whether or not S1 itself has been run. Everything else uses
 * the ranking-prelim rule (content for declared steps, a finished sitting for
 * collaborative ones).
 */
function isPrerequisiteSatisfied(event: SessionEvent, step: StepId): boolean {
  if (step === 'S1') return isStepContentSatisfied(event, 'S1');
  return isRequiredPrelimSatisfiedForRanking(event, step);
}

/**
 * The whole matrix, as a pure function of the three things it depends on.
 * Usable at setup time, before an event exists and before a tool is chosen.
 */
export function stepPrerequisitesFor(
  step: StepId,
  mode: StepMode,
  toolId?: string | null,
): StepId[] {
  const needed = new Set<StepId>();
  // S1 keeps itself here when collaborative, deliberately: a participant tool
  // still needs somebody to run it with.
  if (mode === 'collaborative') needed.add('S1');
  if (step === 'S4') {
    needed.add('S2');
    needed.add('S3');
  }
  if (step === 'S5') {
    needed.add('S2');
    if (toolUsesCriteria(toolId)) {
      needed.add('S3');
      needed.add('S4');
    }
  }
  return STEP_ORDER.filter((s) => needed.has(s));
}

/**
 * The steps `step` cannot be answered without, in the given mode.
 * `mode` defaults to the step's currently configured mode.
 */
export function stepPrerequisites(event: SessionEvent, step: StepId, mode?: StepMode): StepId[] {
  return stepPrerequisitesFor(
    step,
    mode ?? event.steps[step]?.mode ?? 'declared',
    event.steps.S5?.toolId,
  );
}

/** Prerequisites of `step` that are not satisfied yet. */
export function unmetStepPrerequisites(event: SessionEvent, step: StepId, mode?: StepMode): StepId[] {
  return stepPrerequisites(event, step, mode).filter((s) => !isPrerequisiteSatisfied(event, s));
}

const STEP_PREREQ_PHRASE: Record<StepId, string> = {
  S1: 'participants (S1)',
  S2: 'items (S2)',
  S3: 'criteria (S3)',
  S4: 'values (S4)',
  S5: 'a ranking (S5)',
};

function joinPhrases(steps: StepId[]): string {
  const parts = steps.map((s) => STEP_PREREQ_PHRASE[s]);
  if (parts.length <= 1) return parts[0] ?? '';
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

export interface StepModeAvailability {
  available: boolean;
  /** Plain-words reason, present only when the mode is unavailable. */
  reason?: string;
  /** Steps to visit to unblock it. */
  fixAt?: StepId[];
}

/**
 * Can this step be answered this way right now, and if not, why not in words
 * the facilitator can act on?
 */
export function stepModeAvailability(
  event: SessionEvent,
  step: StepId,
  mode: StepMode,
): StepModeAvailability {
  const missing = unmetStepPrerequisites(event, step, mode);
  if (missing.length === 0) return { available: true };

  const rest = missing.filter((s) => s !== 'S1');

  if (mode === 'collaborative' && missing.includes('S1')) {
    const lead =
      step === 'S1'
        ? 'Collaborative needs somebody to collaborate with — add at least one participant here, or run this step declared.'
        : 'Collaborative needs participants — add them in S1, or run this step declared.';
    return {
      available: false,
      reason: rest.length > 0 ? `${lead} It also needs ${joinPhrases(rest)}.` : lead,
      fixAt: missing,
    };
  }

  if (step === 'S4') {
    return {
      available: false,
      reason: `Values need something to score — add ${joinPhrases(missing)} first.`,
      fixAt: missing,
    };
  }

  if (step === 'S5') {
    const toolId = event.steps.S5?.toolId;
    if (toolUsesCriteria(toolId) && missing.some((s) => s === 'S3' || s === 'S4')) {
      const toolName = REGISTRY_TOOL_SPECS_BY_ID[toolId ?? '']?.displayName ?? 'This tool';
      return {
        available: false,
        reason: `${toolName} scores items against criteria, so it needs ${joinPhrases(missing)}. Pick a tool that does not ask for criteria — stack ranking or pairwise — to skip them.`,
        fixAt: missing,
      };
    }
    return {
      available: false,
      reason: `Ranking needs ${joinPhrases(missing)}.`,
      fixAt: missing,
    };
  }

  return {
    available: false,
    reason: `This step needs ${joinPhrases(missing)}.`,
    fixAt: missing,
  };
}

/**
 * True when the event cannot reach its goal without this step.
 *
 * `StepConfig.required` says a step is *in scope* — it appears in the rail and
 * the creator may fill it in. Mandatory is the smaller set: the goal itself and
 * the goal's hard prerequisites. Everything else is an offer, and skipping it
 * is a legitimate way to run an event.
 */
export function isStepMandatory(event: SessionEvent, step: StepId): boolean {
  const goal = event.primaryGoalStep ?? (event.steps.S5?.required ? 'S5' : undefined);
  if (!goal) return event.steps[step]?.required === true;
  if (step === goal) return true;
  return stepPrerequisites(event, goal).includes(step);
}

/** True when this event is going to produce a ranking at all. */
function rankingIsInPlay(event: SessionEvent): boolean {
  // An explicit goal wins: an event whose goal is S2 is not blocked on S5's
  // inputs. Absent one, a required S5 is the frame saying a ranking will run —
  // an event resumed from the Catchball Inbox arrives exactly that way.
  if (event.primaryGoalStep) return event.primaryGoalStep === 'S5';
  return event.steps.S5?.required === true;
}

/**
 * Prelim steps that block the ranking.
 *
 * Declaring an S5-only event means "the inputs are inherited", not "the inputs
 * are optional" — you still cannot rank an empty pool. The set is now S5's hard
 * prerequisites rather than everything the frame happened to mark required, so
 * a ranking with a stack-ranking tool is no longer held up by empty criteria.
 */
export function unmetRequirements(event: SessionEvent): { step: StepId; label: string }[] {
  if (!rankingIsInPlay(event)) return [];
  return unmetStepPrerequisites(event, 'S5')
    .filter((step) => step !== 'S5')
    .map((step) => ({ step, label: stepRequirementLabel(step) }));
}

export interface RequiredInformationItem {
  id: string;
  label: string;
  source: 'job' | 'step';
  step?: StepId;
}

/**
 * Pre-flight checklist for a job-backed event.
 * Job-level requirements describe the user's starting inputs; unmet step
 * requirements describe prototype state that still needs to be filled.
 */
export function resolveRequiredInformation(event: SessionEvent): RequiredInformationItem[] {
  const job = getJob(event.jobId);
  const jobRequirements: RequiredInformationItem[] = (job?.requiredInformation ?? []).map((label) => ({
    id: `job:${label}`,
    label,
    source: 'job',
  }));
  const stepRequirements: RequiredInformationItem[] = unmetRequirements(event).map((requirement) => ({
    id: `step:${requirement.step}`,
    label: requirement.label,
    source: 'step',
    step: requirement.step,
  }));
  return [...jobRequirements, ...stepRequirements];
}

function stepRequirementLabel(step: StepId): string {
  switch (step) {
    case 'S1': return 'Participants not complete';
    case 'S2': return 'Items not complete';
    case 'S3': return 'Criteria not complete';
    case 'S4': return 'Values not complete';
    default: return '';
  }
}

export function canRunRanking(event: SessionEvent): boolean {
  return unmetRequirements(event).length === 0 && event.items.length > 0;
}

// ───────────────────────────────────────────────────────────────────────────
// S1–S5 Dependency matrix (canonized — 01-terminology-and-glossary.md)
//
// ADVISORY ONLY. This is the quality model: to reach full goodness on step X,
// the listed steps should ideally be satisfied first. It drives goodness
// scores, step status and reconfirm prompts — it gates nothing. What a step
// genuinely cannot proceed without is `stepPrerequisites` above; canon's own
// exception ("any step can be declared manually, bypassing the dependency
// gate") is why these two are different lists.
//
// Reading: this is a convergence model — steps inform each other
// iteratively, not a strict linear gate.
//
//   S1 (Participants): needs S3 + S4 to properly scope who should be in the room
//   S2 (Items):        needs S1 + S3 + S4 to determine what's worth ranking
//   S3 (Criteria):     needs S1 only — criteria are evaluation dimensions, independent of items
//   S4 (Values):       needs S1 + S2 + S3 to enter values for the right scope
//   S5 (Ranking):      needs all of S1–S4
// ───────────────────────────────────────────────────────────────────────────

export const STEP_DEPENDENCIES: Record<StepId, StepId[]> = {
  S1: ['S3', 'S4'],
  S2: ['S1', 'S3', 'S4'],
  S3: ['S1'],
  S4: ['S1', 'S2', 'S3'],
  S5: ['S1', 'S2', 'S3', 'S4'],
};

/**
 * Completeness score 0–100 for a step.
 * "Goodness" = step is satisfied + its dependencies are satisfied.
 * Partial credit given when some deps are met.
 */
export function getStepGoodness(event: SessionEvent, step: StepId): number {
  const self = isStepSatisfied(event, step) ? 1 : 0;
  const deps = STEP_DEPENDENCIES[step];
  if (deps.length === 0) return self * 100;
  const depsMetCount = deps.filter((d) => isStepSatisfied(event, d)).length;
  const depScore = depsMetCount / deps.length;
  // Self satisfaction is 60% of score; dep quality is 40%.
  return Math.round((self * 0.6 + depScore * 0.4) * 100);
}

export type StepStatus =
  | 'not_started'    // no data yet, dependencies also not met
  | 'available'      // can meaningfully work on this step now
  | 'in_progress'    // has some data but not yet fully satisfied
  | 'needs_reconfirm' // satisfied, but a dependency that was started may affect this step
  | 'complete';      // satisfied and all required dependencies satisfied

/** Returns true if a step has any data at all (even partial). */
function hasSomeData(event: SessionEvent, step: StepId): boolean {
  switch (step) {
    case 'S1': return event.participants.length > 0;
    case 'S2': return event.items.length > 0;
    case 'S3': return event.criteria.length > 0;
    case 'S4': return Object.keys(event.values).length > 0;
    case 'S5': return event.ranking.length > 0;
    default: return false;
  }
}

/**
 * Step status for conditions-met display and the "next right thing" engine.
 *
 * The status is driven by data presence, not by enforced lock-outs.
 * No step is ever hard-blocked — the designer can always proceed. The
 * status is advisory: it tells them how confident the system is in this step.
 */
export function getStepStatus(event: SessionEvent, step: StepId): StepStatus {
  const satisfied = isStepSatisfied(event, step);
  const deps = STEP_DEPENDENCIES[step];
  const anyDepMet = deps.some((d) => isStepSatisfied(event, d));
  const allDepsMet = deps.every((d) => isStepSatisfied(event, d));

  if (!satisfied) {
    const hasPartialData = hasSomeData(event, step);
    if (hasPartialData) return 'in_progress';
    if (anyDepMet || deps.length === 0) return 'available';
    return 'not_started';
  }

  // Satisfied — only flag needs_reconfirm if an unmet dep has been *started*.
  // If deps are simply empty (never touched), the step is complete for now;
  // no alarm is warranted because nothing changed — it just hasn't been done yet.
  if (!allDepsMet && deps.length > 0) {
    const anyUnmetDepHasData = deps
      .filter((d) => !isStepSatisfied(event, d))
      .some((d) => hasSomeData(event, d));
    if (anyUnmetDepHasData) return 'needs_reconfirm';
  }
  return 'complete';
}

/**
 * "Next right thing" recommendation for the Event Designer console.
 *
 * Scoped to this event's required steps and primary goal — never sends the
 * designer to S5 when the Main Event is S2 (item-pool / cut-line selection).
 *
 * Priority order:
 *   1. Required Frame steps that are in_progress / needs_reconfirm / available
 *   2. Primary goal when Frame content is ready but the Main Event sitting
 *      is not finished (esp. collaborative S2 run)
 *   3. First incomplete required step
 *   4. Primary goal (or S5 as last resort)
 */
export function getNextRightThing(event: SessionEvent): StepId {
  const goal = event.primaryGoalStep ?? 'S5';
  const requiredOrder = (['S1', 'S2', 'S3', 'S4', 'S5'] as StepId[]).filter(
    (s) => event.steps[s]?.required,
  );
  // Frame = required steps other than the goal; for goal=S2, S2 still has a
  // Frame content facet, so treat content-satisfied S2 as frame-done and only
  // return S2 when the collaborative Main Event is still open.
  const frameSteps = requiredOrder.filter((s) => s !== goal);

  const statuses = Object.fromEntries(
    requiredOrder.map((s) => [s, getStepStatus(event, s)]),
  ) as Partial<Record<StepId, StepStatus>>;

  const pick = (pred: (s: StepId) => boolean): StepId | undefined =>
    frameSteps.find(pred) ?? requiredOrder.filter((s) => s !== goal).find(pred);

  const inProgress = pick((s) => statuses[s] === 'in_progress');
  if (inProgress) return inProgress;

  const reconfirm = pick((s) => statuses[s] === 'needs_reconfirm');
  if (reconfirm) return reconfirm;

  const available = pick((s) => statuses[s] === 'available');
  if (available) return available;

  const frameIncomplete = frameSteps.find((s) => statuses[s] !== 'complete');
  if (frameIncomplete) return frameIncomplete;

  // Frame ready → Main Event. For collaborative goals, content alone is not
  // "done" until the sitting runs (or the facilitator declares the pool).
  const goalCfg = event.steps[goal];
  if (goalCfg?.mode === 'collaborative' && goalCfg.runStatus !== 'complete') {
    return goal;
  }
  if (!isStepContentSatisfied(event, goal)) {
    return goal;
  }

  const incomplete = requiredOrder.find((s) => statuses[s] !== 'complete');
  return incomplete ?? goal;
}

/**
 * Human-readable reconfirm prompt for a step that changed.
 * Shown inline when the designer revisits a previously completed step.
 */
export function getReconfirmPrompt(event: SessionEvent, step: StepId): string | null {
  if (getStepStatus(event, step) !== 'needs_reconfirm') return null;
  const unmetDeps = STEP_DEPENDENCIES[step].filter((d) => !isStepSatisfied(event, d));
  if (unmetDeps.length === 0) return null;
  const names = unmetDeps.map((d) => {
    switch (d) {
      case 'S1': return 'participants';
      case 'S2': return 'items';
      case 'S3': return 'criteria';
      case 'S4': return 'values';
      default: return d;
    }
  });
  return `This step was completed, but ${names.join(' and ')} may have changed — worth a review.`;
}

/**
 * Summary of what each step currently has (for the reconfirm snapshot).
 * Shown at the top of a step panel when the designer returns to it.
 */
export function getStepSummary(event: SessionEvent, step: StepId): string | null {
  switch (step) {
    case 'S1': {
      const n = event.participants.length;
      if (n === 0) return null;
      const deciders = event.participants.filter((p) => p.role === 'decider').length;
      return `${n} participant${n !== 1 ? 's' : ''} · ${deciders} decider${deciders !== 1 ? 's' : ''}`;
    }
    case 'S2': {
      const n = event.items.length;
      if (n === 0) return null;
      return `${n} item${n !== 1 ? 's' : ''} in the pool`;
    }
    case 'S3': {
      const n = event.criteria.length;
      if (n === 0) return null;
      const hasWeights = event.criteria.some((c) => c.weight !== 1);
      return `${n} criteri${n !== 1 ? 'a' : 'on'}${hasWeights ? ' · weighted' : ' · equal weight'}`;
    }
    case 'S4': {
      const totalCells = event.items.length * event.criteria.length;
      if (totalCells === 0) return null;
      const filled = event.items.reduce((acc, it) =>
        acc + event.criteria.filter((c) => typeof event.values[it.id]?.[c.id] === 'number').length, 0
      );
      return `${filled} / ${totalCells} values entered (${Math.round((filled / totalCells) * 100)}%)`;
    }
    case 'S5': {
      const n = event.ranking.length;
      if (n === 0) return null;
      return `${n} item${n !== 1 ? 's' : ''} ranked`;
    }
    default:
      return null;
  }
}
